import dayjs from 'dayjs';
import computePortfolioPnlValues from 'src/components/PortfolioPage/PortfolioTradeSubPage/utils/computePortfolioPnlValues';
import {
	BuildPortfolioChartBaseDataParams,
	BuildPortfolioChartLiveDataParams,
} from 'src/components/PortfolioPage/PortfolioTradeSubPage/types';
import { PortfolioPeriod } from 'src/components/PortfolioPage/types';
import computePortfolioTradeDelta from 'src/components/PortfolioPage/utils/computePortfolioTradeDelta';

export function buildIntervalPnlBaseData({
	enabled,
	snapshots,
	includeFeesAndFunding,
	realizedPnlOnly,
}: BuildPortfolioChartBaseDataParams) {
	if (!enabled || !snapshots?.length) {
		return {
			series: [],
			latestSnapshotTotalPnl: 0,
			latestSnapshotFeesAndFunding: 0,
		};
	}

	const series = snapshots.slice(1).map((snapshot, index) => {
		const previousSnapshot = snapshots[index];
		const previousSnapshotDate = new Date(previousSnapshot.ts * 1000);

		if (snapshot.isMockedSnapshot) {
			return {
				date: previousSnapshotDate,
				totalPnl: 0,
				feesAndFunding: undefined,
				isMockedSnapshot: true,
			};
		}

		const {
			totalPnl: currentSnapshotTotalPnl,
			feesAndFunding: currentSnapshotFeesAndFunding,
		} = computePortfolioPnlValues({
			cumulativeRealizedPnl: snapshot.cumulativeRealizedPnl,
			unrealizedPnl: snapshot.unrealizedPnl,
			cumulativeFunding: snapshot.cumulativeFunding,
			cumulativeFeePaid: snapshot.cumulativeFeePaid,
			cumulativeFeeRebate: snapshot.cumulativeFeeRebate,
			includeFeesAndFunding,
			realizedPnlOnly,
		});

		const {
			totalPnl: previousSnapshotTotalPnl,
			feesAndFunding: previousSnapshotFeesAndFunding,
		} = computePortfolioPnlValues({
			cumulativeRealizedPnl: previousSnapshot.cumulativeRealizedPnl,
			unrealizedPnl: previousSnapshot.unrealizedPnl,
			cumulativeFunding: previousSnapshot.cumulativeFunding,
			cumulativeFeePaid: previousSnapshot.cumulativeFeePaid,
			cumulativeFeeRebate: previousSnapshot.cumulativeFeeRebate,
			includeFeesAndFunding,
			realizedPnlOnly,
		});

		const intervalTotalPnl = computePortfolioTradeDelta(
			currentSnapshotTotalPnl,
			previousSnapshotTotalPnl
		);
		const intervalFeesAndFunding = computePortfolioTradeDelta(
			currentSnapshotFeesAndFunding,
			previousSnapshotFeesAndFunding
		);

		return {
			date: previousSnapshotDate,
			totalPnl: intervalTotalPnl,
			feesAndFunding: includeFeesAndFunding
				? intervalFeesAndFunding
				: undefined,
			isMockedSnapshot: false,
		};
	});

	const latestSnapshot = snapshots[snapshots.length - 1];

	const {
		totalPnl: latestSnapshotTotalPnl,
		feesAndFunding: latestSnapshotFeesAndFunding,
	} = computePortfolioPnlValues({
		cumulativeRealizedPnl: latestSnapshot.cumulativeRealizedPnl,
		unrealizedPnl: latestSnapshot.unrealizedPnl,
		cumulativeFunding: latestSnapshot.cumulativeFunding,
		cumulativeFeePaid: latestSnapshot.cumulativeFeePaid,
		cumulativeFeeRebate: latestSnapshot.cumulativeFeeRebate,
		includeFeesAndFunding,
		realizedPnlOnly,
	});

	return {
		series,
		latestSnapshotTotalPnl,
		latestSnapshotFeesAndFunding,
	};
}

interface BuildIntervalPnlLiveDataParams extends BuildPortfolioChartLiveDataParams {
	period: PortfolioPeriod;
	latestSnapshotTotalPnl: number;
	latestSnapshotFeesAndFunding: number;
}

export function buildIntervalPnlLiveData({
	period,
	metrics,
	currentCumulativeFunding,
	currentUnrealizedPnl,
	includeFeesAndFunding,
	realizedPnlOnly,
	enabled,
	latestSnapshotTotalPnl,
	latestSnapshotFeesAndFunding,
}: BuildIntervalPnlLiveDataParams) {
	const liveIntervalDate =
		period === '24h'
			? dayjs().startOf('hour').toDate()
			: dayjs().utc().startOf('day').toDate();

	if (!enabled) {
		return {
			date: liveIntervalDate,
			totalPnl: 0,
			feesAndFunding: undefined,
			isMockedSnapshot: false,
		};
	}

	const { totalPnl: currentTotalPnl, feesAndFunding: currentFeesAndFunding } =
		computePortfolioPnlValues({
			cumulativeRealizedPnl: metrics?.cumulativeRealizedPnl,
			unrealizedPnl: currentUnrealizedPnl,
			cumulativeFunding: currentCumulativeFunding,
			cumulativeFeePaid: metrics?.cumulativeFeePaid,
			cumulativeFeeRebate: metrics?.cumulativeFeeRebate,
			includeFeesAndFunding,
			realizedPnlOnly,
		});

	const liveIntervalTotalPnl = computePortfolioTradeDelta(
		currentTotalPnl,
		latestSnapshotTotalPnl
	);
	const liveIntervalFeesAndFunding = computePortfolioTradeDelta(
		currentFeesAndFunding,
		latestSnapshotFeesAndFunding
	);

	return {
		date: liveIntervalDate,
		totalPnl: liveIntervalTotalPnl,
		feesAndFunding: includeFeesAndFunding
			? liveIntervalFeesAndFunding
			: undefined,
		isMockedSnapshot: false,
	};
}
